import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Search, Trash2 } from 'lucide-react';
import useAppStore from '../store';
import DeleteTaskModal from '../components/modals/DeleteTaskModal';

const ArchivesPage: React.FC = () => {
  const { tasks, drivers, clients, depots, dumpsters, users, currentUser, deleteTask, loadInitialData } = useAppStore();
  const [selectedDriver, setSelectedDriver] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);

  // Get completed and cancelled tasks
  const archivedTasks = tasks.filter(task => 
    task.status === 'completed' || task.status === 'cancelled'
  );

  // Filter and sort tasks based on selected criteria
  const filteredTasks = useMemo(() => {
    return archivedTasks.filter(task => {
      const client = clients.find(c => c.id === task.clientId);
      const dumpster = task.type === 'dumpster' ? dumpsters.find(d => d.id === task.dumpsterId) : null;
      const searchString = `${client?.firstName} ${client?.lastName} ${client?.companyName} ${dumpster?.name} ${dumpster?.number}`.toLowerCase();
      const matchesSearch = !searchTerm || searchString.includes(searchTerm.toLowerCase());
      const matchesDriver = !selectedDriver || task.driverId === selectedDriver;
      const matchesDate = !selectedDate || format(new Date(task.date), 'yyyy-MM-dd') === selectedDate;

      return matchesSearch && matchesDriver && matchesDate;
    }).sort((a, b) => {
      // Sort by date, most recent first
      const dateA = new Date(a.status === 'completed' ? (a.endTime || a.date) : (a.cancelledAt || a.date));
      const dateB = new Date(b.status === 'completed' ? (b.endTime || b.date) : (b.cancelledAt || b.date));
      return dateB.getTime() - dateA.getTime();
    });
  }, [archivedTasks, searchTerm, selectedDriver, selectedDate, clients, dumpsters]);

  // Format the actual quantity with 3 decimal places if it's a decimal number
  const formatQuantity = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'N/A';
    
    // Check if the value has decimal places
    if (value % 1 === 0) {
      return value.toString(); // Return as integer if no decimal places
    } else {
      return value.toFixed(3); // Format with 3 decimal places
    }
  };

  // Handle delete task
  const handleDeleteTask = (task: any) => {
    setSelectedTask(task);
    setShowDeleteModal(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = async (password: string) => {
    if (!selectedTask) return;
    try {
      await deleteTask(selectedTask.id, password);
      setShowDeleteModal(false);
      setSelectedTask(null);
      await loadInitialData();
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <h2 className="text-xl font-bold mb-4">Archives des tâches ({filteredTasks.length})</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Rechercher par client ou benne..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Driver filter */}
          <div>
            <select
              value={selectedDriver}
              onChange={(e) => setSelectedDriver(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous les chauffeurs</option>
              {drivers.map(driver => (
                <option key={driver.id} value={driver.id}>
                  {driver.firstName} {driver.lastName}
                </option>
              ))}
            </select>
          </div>

          {/* Date filter */}
          <div>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Tasks list */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Chauffeur
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Client
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Détails
              </th>
              {currentUser?.role === 'admin' && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredTasks.map((task) => {
              const driver = drivers.find(d => d.id === task.driverId);
              const client = clients.find(c => c.id === task.clientId);
              const finishedByUser = users.find(u => u.id === task.finishedBy);
              
              return (
                <tr key={task.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {format(new Date(task.date), 'dd/MM/yyyy HH:mm', { locale: fr })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {driver ? `${driver.firstName} ${driver.lastName}` : 'N/A'}
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium">{client ? client.companyName : 'N/A'}</div>
                      {client && (
                        <div className="text-sm text-gray-500">
                          {client.firstName} {client.lastName}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {task.type === 'fuel' ? 'Carburant' : 
                     task.type === 'dumpster' ? 'Benne' : 'Phyto'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      task.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {task.status === 'completed' ? 'Terminée' : 'Annulée'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="mb-2 text-sm text-gray-500">
                      {task.status === 'completed' ? (
                        <>
                          <p>Terminée le : {task.endTime ? format(new Date(task.endTime), 'dd/MM/yyyy HH:mm', { locale: fr }) : 'N/A'}</p>
                          {finishedByUser && (
                            <p>Par : {finishedByUser.firstName}</p>
                          )}
                        </>
                      ) : (
                        <p>Annulée le : {task.cancelledAt ? format(new Date(task.cancelledAt), 'dd/MM/yyyy HH:mm', { locale: fr }) : 'N/A'}</p>
                      )}
                    </div>

                    {task.type === 'fuel' && (
                      <div>
                        <p>Type: {
                          task.fuelType === 'fioul' ? 'Fioul' :
                          task.fuelType === 'gnr' ? 'GNR' : 'Gasoil blanc'
                        }</p>
                        <p>Litrage prévu: {task.plannedLiters} L</p>
                        <p>Litrage effectif: {task.actualLiters} L</p>
                        <p>Prix: {task.price} €/L</p>
                      </div>
                    )}

                    {task.type === 'dumpster' && (
                      <div>
                        <p>Benne: {
                          (() => {
                            const dumpster = dumpsters.find(d => d.id === task.dumpsterId);
                            return dumpster ? `${dumpster.name} (${dumpster.number})` : 'N/A';
                          })()
                        }</p>
                        {task.dropLocation && (
                          <p>
                            Déposée: {
                              task.dropLocation.type === 'client' ? 
                                (() => {
                                  const client = clients.find(c => c.id === task.dropLocation?.id);
                                  return client ? client.companyName : 'N/A';
                                })() :
                                (() => {
                                  const depot = depots.find(d => d.id === task.dropLocation?.id);
                                  return depot ? depot.name : 'N/A';
                                })()
                            }
                          </p>
                        )}
                        {task.status === 'completed' && task.actualQuantity !== undefined && (
                          <p className="font-medium text-green-700">
                            Quantité effective: {formatQuantity(task.actualQuantity)} tonnes
                          </p>
                        )}
                      </div>
                    )}

                    {task.type === 'phyto' && (
                      <div>
                        {task.products && task.products.length > 0 ? (
                          task.products.map((product, index) => (
                            <div key={index} className="mb-2">
                              <p>Produit: {product.name}</p>
                              <p>Quantité prévue: {product.plannedQuantity}</p>
                              {product.actualQuantity !== undefined && (
                                <p>Quantité effective: {formatQuantity(product.actualQuantity)}</p>
                              )}
                            </div>
                          ))
                        ) : (
                          <p>Aucun produit</p>
                        )}
                      </div>
                    )}

                    {/* Affichage du commentaire s'il existe */}
                    {task.comment && (
                      <div className="mt-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-700">{task.comment}</p>
                      </div>
                    )}
                  </td>
                  {currentUser?.role === 'admin' && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleDeleteTask(task)}
                        className="text-red-600 hover:text-red-800"
                        title="Supprimer"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Delete Task Modal */}
      {showDeleteModal && selectedTask && (
        <DeleteTaskModal
          task={selectedTask}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedTask(null);
          }}
        />
      )}
    </div>
  );
};

export default ArchivesPage;